import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { cars } from "../data/staticData";

const CarDetail = () => {
  const { carId } = useParams();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Buscar el coche en los datos estáticos
  const car = cars.find(c => c.id === carId);

  if (!car) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="car-detail-not-found">
        <div className="text-center">
          <p className="text-white/60 text-lg mb-4">Vehículo no encontrado</p>
          <Link to="/galeria" className="text-[#00A8FF] hover:underline">
            Volver a la galería
          </Link>
        </div>
      </div>
    );
  }

  const handleNav = (direction) => {
    if (direction === "prev") {
      setCurrentIndex((prev) =>
        prev === 0 ? car.images.length - 1 : prev - 1
      );
    } else {
      setCurrentIndex((prev) =>
        prev === car.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="car-detail-page">
      {/* Back button */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <Link
          to="/galeria"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#00A8FF] transition-colors"
          data-testid="back-to-gallery"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver a la Galería
        </Link>
      </div>

      {/* Hero Image */}
      <section className="relative mb-16">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="relative h-[50vh] overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img
              src={car.thumbnail}
              alt={car.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            <div className="absolute inset-0 border-2 border-[#00A8FF]/20" />
            
            {/* Title overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <motion.h1
                className="text-4xl lg:text-6xl font-black uppercase tracking-tighter text-white"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                {car.name}
              </motion.h1>
              <motion.p
                className="text-white/60 text-lg font-light mt-2 max-w-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                {car.description}
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Galería de fotos */}
      {car.images?.length > 0 && (
        <section className="mb-16">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-black uppercase tracking-tighter mb-8">
                <span className="text-white">Galería de </span>
                <span className="text-[#00A8FF]">Fotos</span>
              </h2>

              <div className="relative" data-testid="image-carousel">
                <div className="relative h-[600px] overflow-hidden border-2 border-[#00A8FF]/30 glow-blue bg-black/50">
                  <motion.img
                    key={currentIndex}
                    src={car.images[currentIndex]}
                    alt={`${car.name} foto ${currentIndex + 1}`}
                    className="w-full h-full object-contain"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  />
                  <div className="absolute top-4 left-4 px-4 py-2 glass text-[#00A8FF] font-bold uppercase tracking-wider">
                    {currentIndex + 1} / {car.images.length}
                  </div>
                </div>

                {car.images.length > 1 && (
                  <>
                    <button
                      onClick={() => handleNav("prev")}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 glass flex items-center justify-center hover:bg-[#00A8FF]/20 transition-colors"
                      data-testid="carousel-prev"
                    >
                      <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                    <button
                      onClick={() => handleNav("next")}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 glass flex items-center justify-center hover:bg-[#00A8FF]/20 transition-colors"
                      data-testid="carousel-next"
                    >
                      <ChevronRight className="w-6 h-6 text-white" />
                    </button>
                  </>
                )}

                {/* Dots */}
                <div className="flex justify-center gap-2 mt-4">
                  {car.images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentIndex(i)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        i === currentIndex ? "bg-[#00A8FF]" : "bg-white/20"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Thumbnails */}
              <div className="grid grid-cols-4 gap-4 mt-6">
                {car.images.map((image, i) => (
                  <motion.button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`relative h-24 overflow-hidden border-2 transition-all ${
                      i === currentIndex
                        ? "border-[#00A8FF] glow-blue"
                        : "border-white/10 hover:border-white/30"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img
                      src={image}
                      alt={`${car.name} miniatura ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Services Applied */}
      {car.services_applied?.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              className="glass-card p-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-black uppercase tracking-tighter mb-6">
                <span className="text-white">Servicios </span>
                <span className="text-[#00A8FF]">Aplicados</span>
              </h2>
              <div className="flex flex-wrap gap-4">
                {car.services_applied.map((service, i) => (
                  <motion.div
                    key={i}
                    className="px-6 py-3 glass border border-[#00A8FF]/30 text-white font-medium hover:border-[#00A8FF] hover:text-[#00A8FF] transition-colors"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    {service}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CarDetail;
